import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authRepository } from '../repositories/auth.repository'
import { usersRepository } from '../repositories/users.repository'
import { useMutationTracker } from '../hooks/useMutationTracker'
import type { LoginRequest, RegisterRequest, VerifyEmailRequest, TelegramLoginRequest } from '../models/auth'

const USER_KEY = 'user'
const LOGIN_KEY = 'login'
const REGISTER_KEY = 'register'
const VERIFY_EMAIL_KEY = 'verify-email'
const TELEGRAM_LOGIN_KEY = 'telegram-login'
const LOGOUT_KEY = 'logout'

export const useAuth = () => {
  const queryClient = useQueryClient()

  // Get current user
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser
  } = useQuery({
    queryKey: [USER_KEY],
    queryFn: usersRepository.getMe,
    retry: false,
    enabled: !!localStorage.getItem('token')
  })

  // Login mutation
  const loginMutation = useMutation({
    mutationKey: [LOGIN_KEY],
    mutationFn: (data: LoginRequest) => authRepository.login(data),
    onSuccess: (response) => {
      localStorage.setItem('token', response.data.token)
      queryClient.setQueryData([USER_KEY], response.data.user)
      queryClient.invalidateQueries({ queryKey: [USER_KEY] })
    }
  })

  // Register mutation
  const registerMutation = useMutation({
    mutationKey: [REGISTER_KEY],
    mutationFn: (data: RegisterRequest) => authRepository.register(data),
    onSuccess: (response) => {
      localStorage.setItem('token', response.data.token)
      queryClient.setQueryData([USER_KEY], response.data.user)
      queryClient.invalidateQueries({ queryKey: [USER_KEY] })
    }
  })

  // Verify email mutation
  const verifyEmailMutation = useMutation({
    mutationKey: [VERIFY_EMAIL_KEY],
    mutationFn: (data: VerifyEmailRequest) => authRepository.verifyEmail(data)
  })

  // Telegram login mutation
  const telegramLoginMutation = useMutation({
    mutationKey: [TELEGRAM_LOGIN_KEY],
    mutationFn: (data: TelegramLoginRequest) => authRepository.telegramLogin(data),
    onSuccess: (response) => {
      localStorage.setItem('token', response.data.token)
      queryClient.setQueryData([USER_KEY], response.data.user)
      queryClient.invalidateQueries({ queryKey: [USER_KEY] })
    }
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationKey: [LOGOUT_KEY],
    mutationFn: authRepository.logout,
    onSuccess: () => {
      localStorage.removeItem('token')
      queryClient.clear()
    }
  })

  // Track mutation states
  const loginState = useMutationTracker(LOGIN_KEY)
  const registerState = useMutationTracker(REGISTER_KEY)
  const verifyEmailState = useMutationTracker(VERIFY_EMAIL_KEY)
  const telegramLoginState = useMutationTracker(TELEGRAM_LOGIN_KEY)
  const logoutState = useMutationTracker(LOGOUT_KEY)

  const isAuthenticated = !!user && !!localStorage.getItem('token')

  return {
    // Data
    user,
    isAuthenticated,
    
    // Loading and error states
    dataIsLoading: userLoading,
    dataError: userError,
    
    // Actions
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    verifyEmail: verifyEmailMutation.mutate,
    telegramLogin: telegramLoginMutation.mutate,
    logout: logoutMutation.mutate,
    refetchUser,
    
    // Mutation states
    loginLoading: loginState.isPending,
    loginError: loginState.error,
    loginSuccess: loginState.isSuccess,
    
    registerLoading: registerState.isPending,
    registerError: registerState.error,
    registerSuccess: registerState.isSuccess,
    
    verifyEmailLoading: verifyEmailState.isPending,
    verifyEmailError: verifyEmailState.error,
    verifyEmailSuccess: verifyEmailState.isSuccess,
    
    telegramLoginLoading: telegramLoginState.isPending,
    telegramLoginError: telegramLoginState.error,
    telegramLoginSuccess: telegramLoginState.isSuccess,
    
    logoutLoading: logoutState.isPending,
    logoutError: logoutState.error,
    logoutSuccess: logoutState.isSuccess
  }
}